import { StoryCard } from './StoryCard';
import { Search, TrendingUp } from 'lucide-react';
import { useState } from 'react';

interface ExploreProps {
  stories: any[];
  genres: any[];
  onPlayStory: (story: any) => void;
  onLikeStory: (storyId: string) => void;
  likedStories: Set<string>;
}

export function Explore({ stories, genres, onPlayStory, onLikeStory, likedStories }: ExploreProps) {
  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredStories = selectedGenre
    ? stories.filter(story => story.genreIds?.includes(selectedGenre))
    : stories;

  const searchedStories = searchQuery
    ? filteredStories.filter(story =>
        story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        story.author.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredStories;

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="page-header gap-4">
        <h1 className="text-4xl font-bold">Explore</h1>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/50" />
          <input
            type="text"
            placeholder="Search stories, authors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-4 rounded-2xl bg-base-200 focus:bg-base-100 focus:outline-none focus:ring-2 focus:ring-base-content/10 transition-all"
          />
        </div>
      </div>

      {/* Genres */}
      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4">Browse by Genre</h2>
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
          <button
            onClick={() => setSelectedGenre(null)}
            className={`btn btn-sm whitespace-nowrap ${
              selectedGenre === null ? 'btn-primary' : 'btn-outline btn-primary'
            }`}
          >
            All Stories
          </button>
          {genres.map((genre) => (
            <button
              key={genre.id}
              onClick={() => setSelectedGenre(genre.id)}
              className={`btn btn-sm whitespace-nowrap ${
                selectedGenre === genre.id ? 'btn-primary' : 'btn-outline btn-primary'
              }`}
            >
              {genre.name}
            </button>
          ))}
        </div>
      </section>

      {/* Featured Genre Cards */}
      {!selectedGenre && !searchQuery && (
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-4">Featured Collections</h2>
          <div className="grid grid-cols-2 gap-4">
            {genres.slice(0, 4).map((genre) => {
              const genreStories = stories.filter(s => s.genreIds?.includes(genre.id));
              return (
                <div
                  key={genre.id}
                  onClick={() => setSelectedGenre(genre.id)}
                  className="relative h-32 rounded-2xl overflow-hidden cursor-pointer group bg-base-200 hover:bg-base-300 transition-colors"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral/20 to-neutral/50 group-hover:from-neutral/30 group-hover:to-neutral/60 transition-all" />
                  <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div>
                      <h3 className="font-bold text-neutral-content">{genre.name}</h3>
                      <p className="text-xs text-neutral-content/80 mt-1">{genreStories.length} stories</p>
                    </div>
                    <TrendingUp className="w-5 h-5 text-neutral-content/70 self-end" />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Stories Grid */}
      <section>
        <h2 className="text-xl font-bold mb-4">
          {selectedGenre
            ? genres.find(g => g.id === selectedGenre)?.name
            : searchQuery
            ? `Results for "${searchQuery}"`
            : 'All Stories'}
        </h2>
        {searchedStories.length === 0 ? (
          <div className="text-center py-16 text-base-content/60">
            <p>No stories found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-6">
            {searchedStories.map((story) => (
              <StoryCard
                key={story.id}
                story={story}
                onPlay={onPlayStory}
                onLike={onLikeStory}
                isLiked={likedStories.has(story.id)}
                size="large"
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
